/**
 * Dash PWA Manager Component
 *
 * Main PWA manager for the dashboard shell (legacy `dash.*` or apex `/dashboard`).
 * Coordinates install prompts, offline indicators, update notifications,
 * and platform-specific instructions.
 *
 * @author AI Assistant
 * @version 2.0.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { logger } from "../../utils/logger";
import { Snackbar, Alert, Button, Box, AlertTitle } from '@mui/material';
import { Refresh, CloudDone } from '@mui/icons-material';
import { OfflineIndicator } from './OfflineIndicator';
import { DashInstallPrompt } from './DashInstallPrompt';
import { offlineDashboardService } from '../../services/offlineDashboardService';
import { backgroundSyncService } from '../../services/backgroundSyncService';
import { trackEvent } from '../../utils/analytics';
import { logger } from "../../utils/logger";
import {
  applyPwaServiceWorkerUpdate,
  attachPwaRegistrationUpdateChecks,
} from '../../utils/pwaServiceWorkerUpdate';
// =============================================================================
// TYPES
// =============================================================================
interface DashPWAManagerProps {
    children?: React.ReactNode;
    showOfflineIndicator?: boolean;
    showInstallPrompt?: boolean;
    autoSync?: boolean;
}
// =============================================================================
// DASH PWA MANAGER COMPONENT
// =============================================================================
export const DashPWAManager: React.FC<DashPWAManagerProps> = ({ children, showOfflineIndicator = true, showInstallPrompt = true, autoSync = true }) => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [syncCompleted, setSyncCompleted] = useState(false);
    const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState<ServiceWorkerRegistration | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const dismissedUpdatePromptRef = useRef(false);

    const showDashUpdate = useCallback((signal: 'waiting' | 'controller') => {
        if (dismissedUpdatePromptRef.current || !navigator.serviceWorker.controller) {
            return;
        }
        setUpdateAvailable(true);
        trackEvent({
            action: 'pwa_update_available',
            category: 'pwa',
            label: 'dash',
            custom_parameters: { signal },
        });
    }, []);

    useEffect(() => {
        if (!serviceWorkerRegistration) {
            return;
        }
        return attachPwaRegistrationUpdateChecks(serviceWorkerRegistration, showDashUpdate);
    }, [serviceWorkerRegistration, showDashUpdate]);

    // =============================================================================
    // INITIALIZATION
    // =============================================================================
    useEffect(() => {
        initializePWA();
    }, []);
    const initializePWA = async () => {
        try {
            const isDevHost = import.meta.env.DEV || window.location.port === '5173';
            if (isDevHost && 'serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map(registration => registration.unregister()));
                logger.debug('ℹ️ Dash PWA disabled in dev host');
                setIsInitialized(true);
                return;
            }
            // Initialize offline dashboard service
            await offlineDashboardService.initialize();
            logger.debug('✅ Offline Dashboard Service initialized');
            // Register service worker for dash subdomain
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.register('/dash-sw.js', {
                    scope: '/'
                });
                setServiceWorkerRegistration(registration);
                logger.debug('✅ Dash Service Worker registered');
            }
            // Start auto-sync if enabled
            if (autoSync) {
                backgroundSyncService.startAutoSync(10 * 60 * 1000); // Every 10 minutes
            }
            // Listen to sync events
            backgroundSyncService.addEventListener(handleSyncEvent);
            setIsInitialized(true);
            trackEvent('pwa_initialized', { subdomain: 'dash' });
        }
        catch (error) {
            logger.error('❌ Failed to initialize PWA:', error, {});
            trackEvent('pwa_initialization_error', { error: String(error) });
        }
    };
    // =============================================================================
    // EVENT HANDLERS
    // =============================================================================
    const handleSyncEvent = (event: string, data?: any) => {
        switch (event) {
            case 'completed':
                setSyncCompleted(true);
                setTimeout(() => setSyncCompleted(false), 3000);
                trackEvent('pwa_sync_completed', data);
                break;
            case 'failed':
                trackEvent('pwa_sync_failed', data);
                break;
        }
    };
    const handleUpdate = async () => {
        if (!serviceWorkerRegistration) {
            window.location.reload();
            return;
        }
        setUpdateAvailable(false);
        dismissedUpdatePromptRef.current = false;
        trackEvent({
            action: 'pwa_update_applied',
            category: 'pwa',
            label: 'dash',
        });
        await applyPwaServiceWorkerUpdate(serviceWorkerRegistration);
    };
    const handleInstalled = () => {
        logger.debug('✅ PWA installed');
        trackEvent('pwa_install_completed', { subdomain: 'dash' });
    };
    const handleInstallDismissed = () => {
        logger.debug('ℹ️ Install prompt dismissed');
    };
    // =============================================================================
    // CLEANUP
    // =============================================================================
    useEffect(() => {
        return () => {
            backgroundSyncService.removeEventListener(handleSyncEvent);
            if (autoSync) {
                backgroundSyncService.stopAutoSync();
            }
        };
    }, [autoSync]);
    // =============================================================================
    // RENDER
    // =============================================================================
    if (!isInitialized) {
        return <>{children}</>;
    }
    return (<>
      {children}

      {/* Offline Indicator */}
      {showOfflineIndicator && <OfflineIndicator position="bottom"/>}

      {/* Install Prompt */}
      {showInstallPrompt && (<DashInstallPrompt onInstalled={handleInstalled} onDismissed={handleInstallDismissed}/>)}

      {/* Update Available Notification */}
      <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown' || reason === 'clickaway') {
            dismissedUpdatePromptRef.current = true;
            setUpdateAvailable(false);
          }
        }}
      >
        <Alert severity="info" variant="filled" action={<Button color="inherit" size="small" startIcon={<Refresh />} onClick={handleUpdate}>
              Update Now
            </Button>}>
          <AlertTitle>Update Available</AlertTitle>
          A new version of APT Dashboard is ready. Update now for the latest features.
        </Alert>
      </Snackbar>

      {/* Sync Completed Notification */}
      <Snackbar open={syncCompleted} autoHideDuration={3000} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} onClose={() => setSyncCompleted(false)}>
        <Alert severity="success" variant="filled" icon={<CloudDone />}>
          All changes synced successfully
        </Alert>
      </Snackbar>
    </>);
};
export default DashPWAManager;
